import { CircleCheck, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/components/visitone/visitone-provider'
import { Button } from '@/components/ui/button'
import { usePageTitle } from '@/hooks/use-page-title'

export type OperationState = 'loading' | 'progress' | 'success' | 'error'

/*
 * OperationFeedback: persistent feedback for one standalone async operation.
 * Unknown total uses indeterminate loading; a known total uses a native
 * semantic progress element with a visible localised count. Loading, progress
 * and success announce politely; failure is persistent and assertive, and
 * Retry is the only focusable control. A failure without `onRetry` has no
 * Retry, for when the detail says how to try again from the page itself.
 * `pageHeading` makes the title the page's `h1` and document title, for a
 * screen that consists only of this feedback.
 */
export function OperationFeedback({
  state,
  title,
  detail,
  current = 48,
  total = 120,
  onRetry,
  pageHeading = false,
}: {
  state: OperationState
  title: string
  detail?: string
  current?: number
  total?: number
  onRetry?: () => void
  pageHeading?: boolean
}) {
  const t = useT()
  usePageTitle(pageHeading ? title : null)
  const Title = pageHeading ? 'h1' : 'p'
  const percent = total > 0 ? Math.round((current / total) * 100) : 0
  const isError = state === 'error'

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      className={cn(
        'flex max-w-[40rem] flex-col gap-3 rounded-md border bg-surface p-4',
        isError ? 'border-danger/60' : 'border-line',
      )}
    >
      <div className="flex items-center gap-2">
        {state === 'success' ? (
          <CircleCheck aria-hidden="true" className="size-5 shrink-0 text-success" />
        ) : null}
        {isError ? <TriangleAlert aria-hidden="true" className="size-5 shrink-0 text-danger" /> : null}
        <Title className={cn('text-sm font-medium wrap-anywhere', isError ? 'text-danger' : 'text-fg-strong')}>
          {title}
        </Title>
      </div>

      {detail ? <p className="text-sm text-fg-muted wrap-anywhere">{detail}</p> : null}

      {state === 'loading' ? (
        <progress className="h-1.5 w-full overflow-hidden rounded-lg" aria-label={title} />
      ) : null}

      {state === 'progress' ? (
        <div className="flex flex-col gap-1.5">
          <progress
            className="h-1.5 w-full overflow-hidden rounded-lg"
            value={current}
            max={total}
            aria-label={title}
          />
          <p className="text-xs text-fg-muted tabular-nums">
            {t('core.operationFeedback.progressCount', { current, total })} · {percent}%
          </p>
        </div>
      ) : null}

      {isError && onRetry ? (
        <div>
          <Button variant="primary" size="sm" onClick={onRetry}>
            {t('core.operationFeedback.retry')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
